import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedRichText, LocalizedText } from "@/i18n/LocalizedText";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowRight,
  ChartNoAxesColumnIncreasing,
  Check,
  Info,
  ListChecks,
  LogOut,
  Network,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CircularProgress } from "@/components/ui/circularProgress";
import { Skeleton } from "@/components/ui/skeleton";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { steps } from "@/components/Register";
import { Separator } from "@/components/ui/separator";
import registrationBg from "../assets/registrationBg.svg";
import noboriLogo from "../assets/logo.svg";
import { BorderLoaderLogo } from "@/components/AnimationLogo";
import { useAuth } from "@/components/AuthContext";
import { capturePosthogEvent } from "@/lib/posthog";
import { visabilityAPI, type OnboardingSnapshotData } from "@/services/api";
import { useQuery } from "@tanstack/react-query";
import "./style.css";

const SNAPSHOT_SAMPLE_SIZE = 8;
const DESIGN_NEXT_STEPS = [
  { title: "Get listed in comparison pages" },
  { title: "Create “alternatives” content" },
  { title: "Expand presence in top sources" },
  { title: "Expand presence in top sources" },
];

const SNAPSHOT_LOADING_STEPS = [
  {
    label: "Analyzing your selected prompts",
    icon: ListChecks,
  },
  {
    label: "Scanning AI responses for mentions",
    icon: Sparkles,
  },
  {
    label: "Mapping cited sources and competitors",
    icon: Network,
  },
  {
    label: "Calculating your visibility score",
    icon: ChartNoAxesColumnIncreasing,
  },
];

const SNAPSHOT_CACHE_PREFIX = "nobori_onboarding_snapshot_v1";
const SNAPSHOT_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

type OnboardingSnapshotCachePayload = {
  storedAt: number;
  snapshot: OnboardingSnapshotData;
};

const buildOnboardingSnapshotCacheKey = (userId: string, brandId: string) => {
  if (!userId || !brandId) return "";
  return `${SNAPSHOT_CACHE_PREFIX}:${userId}:${brandId}:${SNAPSHOT_SAMPLE_SIZE}`;
};

const readOnboardingSnapshotCache = (key: string) => {
  if (!key || typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    const payload = JSON.parse(raw) as Partial<OnboardingSnapshotCachePayload>;
    if (!payload?.snapshot || typeof payload.storedAt !== "number") {
      window.localStorage.removeItem(key);
      return null;
    }

    if (Date.now() - payload.storedAt > SNAPSHOT_CACHE_MAX_AGE_MS) {
      window.localStorage.removeItem(key);
      return null;
    }

    return payload.snapshot;
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }
};

const writeOnboardingSnapshotCache = (
  key: string,
  snapshot: OnboardingSnapshotData,
) => {
  if (!key || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        storedAt: Date.now(),
        snapshot,
      } satisfies OnboardingSnapshotCachePayload),
    );
  } catch {
    // Local storage can be unavailable in private or constrained browser modes.
  }
};

const getSnapshotEventProps = (
  snapshot: OnboardingSnapshotData | null,
  brandId: string,
) => ({
  brand_id: snapshot?.meta?.brand_id || brandId || null,
  sample_size_used: snapshot?.meta?.sample_size_used ?? null,
  is_partial: snapshot?.meta?.is_partial ?? null,
  citations_available: snapshot?.cited_domains?.available ?? null,
  providers_used: snapshot?.providers?.used ?? [],
});

const resolveDomain = (value?: string | null) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const normalized = raw.includes("://") ? raw : `https://${raw}`;
    return new URL(normalized).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return raw
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split(/[/?#:]/)[0]
      .toLowerCase();
  }
};

const getFaviconUrl = (domain?: string | null) =>
  `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${resolveDomain(
    domain,
  )}&size=64`;

const SkeletonRow = ({ dimmed = false }: { dimmed?: boolean }) => (
  <div
    className={`flex h-12 min-w-0 flex-1 items-center ${dimmed ? "opacity-40" : ""}`}
  >
    <div className="flex h-12 w-16 shrink-0 items-center pr-4">
      <Skeleton className="h-12 w-12 rounded-full bg-[#F5F5F5]" />
    </div>
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <Skeleton className="h-4 w-full rounded-full bg-[#F5F5F5]" />
      <Skeleton className="h-4 w-[92%] rounded-full bg-[#F5F5F5]" />
    </div>
  </div>
);

const SkeletonRowsGrid = ({
  rows,
  columns,
}: {
  rows: number;
  columns: 1 | 2;
}) => (
  <div className="flex flex-col gap-5">
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div
        key={rowIndex}
        className={`grid gap-5 ${columns === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}
      >
        {Array.from({ length: columns }).map((__, columnIndex) => (
          <SkeletonRow
            key={`${rowIndex}-${columnIndex}`}
            dimmed={(rowIndex + columnIndex) % 2 === 1}
          />
        ))}
      </div>
    ))}
  </div>
);

const OnboardingVisibilitySkeleton = () => (
  <div
    aria-label="Loading AI visibility snapshot"
    className="mb-10 flex flex-col gap-6"
  >
    <div className="rounded-xl bg-white px-6 py-6 shadow-[0px_1px_3px_rgba(0,0,0,0.1)]">
      <SkeletonRowsGrid rows={2} columns={2} />
    </div>
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div className="rounded-xl bg-white px-6 py-6 shadow-[0px_1px_3px_rgba(0,0,0,0.1)]">
        <SkeletonRowsGrid rows={3} columns={1} />
      </div>
      <div className="rounded-xl bg-white px-6 py-6 shadow-[0px_1px_3px_rgba(0,0,0,0.1)]">
        <SkeletonRowsGrid rows={3} columns={1} />
      </div>
    </div>
    <div className="rounded-xl bg-white px-6 py-6 shadow-[0px_1px_3px_rgba(0,0,0,0.1)]">
      <SkeletonRowsGrid rows={3} columns={2} />
    </div>
  </div>
);

const OnboardingVisibilityBrandIcon = ({
  brandDomain,
}: {
  brandDomain: string;
}) => (
  <div className="relative mb-12 h-16 w-16 overflow-visible rounded-2xl">
    <BorderLoaderLogo className="z-10 rounded-2xl pointer-events-none" />
    <div className="relative h-16 w-16 overflow-hidden rounded-2xl bg-white">
      <img
        src={getFaviconUrl(brandDomain)}
        alt="company logo"
        className="h-full w-full object-cover"
      />
      <div className="absolute -top-[1px] left-0 h-1/5 w-full rounded-t-2xl bg-gradient-to-b from-[#906870] to-transparent pointer-events-none" />
      <div className="absolute top-0 right-0 h-full w-1/5 bg-gradient-to-l from-[#906870] to-transparent pointer-events-none" />
    </div>
  </div>
);

const OnboardingVisibilitySidebarLoading = ({
  brandName,
  brandDomain,
  activeStep,
}: {
  brandName: string;
  brandDomain: string;
  activeStep: number;
}) => (
  <div className="w-full pt-16">
    <OnboardingVisibilityBrandIcon brandDomain={brandDomain} />

    <p className="mb-7 w-full text-2xl font-medium tracking-wide text-white">
      <LocalizedText id="legacy.creating.your.ai.visibility.snapshot.x6pvyv" />
    </p>
    <p className="mb-12 max-w-md text-xl leading-8 text-[#737373]">
      <LocalizedRichText
        id="visibilitySnapshot.loading.description"
        values={{
          brand: (
            <span className="text-[#FF5E00]">
              {brandName || <LocalizedText id="legacy.your.brand.8dvymn" />}
            </span>
          ),
        }}
      />
    </p>

    <div className="mb-12 flex w-full flex-col gap-7">
      {SNAPSHOT_LOADING_STEPS.map((step, index) => {
        const Icon = step.icon;
        const isActive = index === activeStep;
        const isComplete = index < activeStep;
        return (
          <div
            key={step.label}
            className={`flex items-center gap-5 transition-all duration-500 ${
              isActive
                ? "translate-x-0 text-[#FAFAFA] opacity-100"
                : isComplete
                  ? "text-[#A3A3A3] opacity-55"
                  : "text-[#737373] opacity-35"
            }`}
          >
            <span
              className={`relative flex h-4 w-4 shrink-0 items-center justify-center ${
                isActive ? "text-[#FAFAFA]" : ""
              }`}
            >
              {isActive ? (
                <span className="absolute h-6 w-6 rounded-full bg-white/10 animate-ping" />
              ) : null}
              <Icon size={16} strokeWidth={1.5} className="relative z-10" />
            </span>
            <span className="text-base">{step.label}</span>
          </div>
        );
      })}
    </div>

    <div className="w-full max-w-md">
      <p className="text-sm text-[#737373]">
        <LocalizedText id="legacy.this.usually.takes.10.20.seconds.7b2fgt" />
      </p>
    </div>
  </div>
);

export const SeeYourVisibility = ({
  setSeeYourVisibility,
  handleSignOut,
  brandId,
  previewSnapshot,
  previewBrandName,
  previewBrandDomain,
}: {
  setSeeYourVisibility: (i: boolean) => void;
  handleSignOut: () => void;
  brandId?: string;
  previewSnapshot?: OnboardingSnapshotData;
  previewBrandName?: string;
  previewBrandDomain?: string;
}) => {
  const { t } = useLocale();
  const { user } = useAuth();
  const step = 4;

  const selectedBrand = useMemo(() => {
    const brands = Array.isArray(user?.brands) ? user.brands : [];
    if (!brands.length) return null;
    if (!brandId) return brands[0];
    return (
      brands.find(
        (item: any) =>
          String(item?.brandId || item?.id || "") === String(brandId),
      ) || brands[0]
    );
  }, [brandId, user?.brands]);

  const resolvedBrandId = String(
    selectedBrand?.brandId || selectedBrand?.id || brandId || "",
  ).trim();
  const brandName = String(
    previewBrandName || selectedBrand?.name || "your brand",
  ).trim();
  const brandDomain = resolveDomain(
    previewBrandDomain || selectedBrand?.url || "",
  );
  const snapshotCacheKey = useMemo(
    () =>
      buildOnboardingSnapshotCacheKey(
        String(user?.id || user?.email || ""),
        resolvedBrandId,
      ),
    [resolvedBrandId, user?.email, user?.id],
  );
  const cachedSnapshot = useMemo(
    () => readOnboardingSnapshotCache(snapshotCacheKey),
    [snapshotCacheKey],
  );
  const [loadedSnapshot, setLoadedSnapshot] =
    useState<OnboardingSnapshotData | null>(cachedSnapshot);
  const [activeSnapshotLoadingStep, setActiveSnapshotLoadingStep] = useState(0);
  const viewedEventRef = useRef<string>("");
  const loadedEventRef = useRef<string>("");

  const snapshotQuery = useQuery({
    queryKey: [
      "onboardingSnapshot",
      "stable",
      resolvedBrandId,
      SNAPSHOT_SAMPLE_SIZE,
    ],
    queryFn: async () =>
      visabilityAPI.getOnboardingSnapshot({
        brandId: resolvedBrandId || undefined,
        sampleSize: SNAPSHOT_SAMPLE_SIZE,
      }),
    enabled: Boolean(resolvedBrandId) && !previewSnapshot && !cachedSnapshot,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const querySnapshot: OnboardingSnapshotData | null =
    previewSnapshot ?? snapshotQuery.data?.snapshot ?? null;
  const querySnapshotStatus = querySnapshot?.meta?.status || "pending";
  const snapshot = loadedSnapshot;

  useEffect(() => {
    setLoadedSnapshot(cachedSnapshot);
  }, [cachedSnapshot, resolvedBrandId]);

  useEffect(() => {
    if (!querySnapshot || querySnapshotStatus === "pending") return;
    setLoadedSnapshot((current) => current ?? querySnapshot);
    writeOnboardingSnapshotCache(snapshotCacheKey, querySnapshot);
  }, [querySnapshot, querySnapshotStatus, snapshotCacheKey]);

  useEffect(() => {
    if (!resolvedBrandId) return;
    if (viewedEventRef.current === resolvedBrandId) return;
    viewedEventRef.current = resolvedBrandId;
    capturePosthogEvent(
      "onboarding_snapshot_viewed",
      getSnapshotEventProps(snapshot, resolvedBrandId),
    );
  }, [resolvedBrandId, snapshot]);

  useEffect(() => {
    if (!snapshot) return;
    const eventKey = `${snapshot.meta.brand_id}:${snapshot.meta.generated_at}:${snapshot.meta.status}`;
    if (loadedEventRef.current === eventKey) return;
    loadedEventRef.current = eventKey;
    capturePosthogEvent(
      "onboarding_snapshot_loaded",
      getSnapshotEventProps(snapshot, resolvedBrandId),
    );
  }, [resolvedBrandId, snapshot]);

  const handleStartTrial = () => {
    capturePosthogEvent(
      "onboarding_snapshot_cta_clicked",
      getSnapshotEventProps(snapshot, resolvedBrandId),
    );
    setSeeYourVisibility(false);
  };

  const visibilityPercentage = Math.max(
    0,
    Math.min(100, Number(snapshot?.visibility?.percentage ?? 0)),
  );
  const competitors = snapshot?.comentioned_brands?.length
    ? snapshot.comentioned_brands.slice(0, 4).map((item) => ({
        title: item.name,
        url: item.domain || item.name,
        percentage: Math.round(item.share_percent),
        averagePosition: item.average_position ?? null,
      }))
    : [];
  const citations =
    snapshot?.cited_domains?.available && snapshot.cited_domains.items.length
      ? snapshot.cited_domains.items.slice(0, 4).map((item) => ({
          url: item.domain,
        }))
      : [];
  const nextsteps = DESIGN_NEXT_STEPS;
  const shouldShowSnapshotSkeleton =
    !snapshotQuery.isError && Boolean(resolvedBrandId) && !snapshot;

  useEffect(() => {
    if (!shouldShowSnapshotSkeleton) {
      setActiveSnapshotLoadingStep(0);
      return;
    }

    const interval = window.setInterval(() => {
      setActiveSnapshotLoadingStep(
        (current) => (current + 1) % SNAPSHOT_LOADING_STEPS.length,
      );
    }, 2200);

    return () => window.clearInterval(interval);
  }, [shouldShowSnapshotSkeleton]);

  const highestCompetitorVisibility = competitors.reduce(
    (highest, item) => Math.max(highest, Number(item.percentage) || 0),
    0,
  );
  const hasCompetitorAdvantage =
    highestCompetitorVisibility > visibilityPercentage;
  const averageTargetPosition = snapshot?.visibility?.average_position ?? null;
  const completedPromptCount = snapshot?.meta?.sample_size_used ?? 0;
  const positionSummary =
    typeof averageTargetPosition === "number" && averageTargetPosition > 0
      ? `Average position #${averageTargetPosition} when mentioned`
      : visibilityPercentage > 0
        ? "Position unavailable in this sample"
        : "Not visible in this sample";

  return (
    <div className="d-flex flex-column-fluid flex-row-auto justify-content-center mx-auto">
      <div className="bg-[#f5f5f5] grid grid-cols-1 lg:grid-cols-2 w-full">
        <div className={` flex flex-col items-center  mb-10`}>
          <div className={`flex w-full justify-center flex-col`}>
            <div className="sticky top-0 pt-10 z-50 backdrop-blur-md [mask-image:linear-gradient(to_bottom,black_85%,transparent_100%)]">
              <div className="flex justify-center mb-6">
                <img src={noboriLogo}></img>
              </div>

              <div className="flex justify-between text-sm mx-0 mt-10 mx-auto mb-6 max-w-[400px]">
                {steps.map((st) => {
                  return (
                    <React.Fragment key={st.number}>
                      <div
                        className={`flex flex-col items-center text-[#393B40] px-3 md:px-0 ${step + 1 >= st.number && "text-[#FF5E00]"}  `}
                      >
                        <div
                          className={`flex justify-center items-center w-[32px] h-[32px] border rounded-full border-[1.5px]  ${step + 1 >= st.number && "border-[#FF5E00] font-medium"} ${step + 1 > st.number && "bg-[#FF5E00]"}`}
                        >
                          {step + 1 > st.number ? (
                            <Check className="text-white" />
                          ) : (
                            `0${st.number}`
                          )}
                        </div>
                        <div
                          className={`text-[#737373] font-medium text-sm hidden md:block ${step + 1 >= st.number && "text-[#FF5E00]"} `}
                        >
                          {st.name}
                        </div>
                      </div>
                      {st.number <= 3 && (
                        <div className="grow relative translate-y-1/3">
                          <Separator
                            className={`${st.number < step + 1 && "bg-[#FF5E00]"} `}
                          />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <div className={`mx-auto px-5 sm:min-w-[685px]`}>
              {shouldShowSnapshotSkeleton ? (
                <OnboardingVisibilitySkeleton />
              ) : (
                <>
                  <div
                    className={`relative bg-white border-l border-r border-b relative flex-wrap md:flex-nowrap w-full flex gap-4 md:gap-12 p-8 md:p-8 mb-6 rounded-3xl bg-[right_bottom] bg-no-repeat`}
                  >
                    <div className="absolute top-0 left-0 right-0 h-[20px] bg-gradient-to-b from-[#f5f5f5] to-white pointer-events-none" />

                    <div
                      className={`flex flex-col justify-center w-full relative`}
                    >
                      <p className="text-[#0a0a0a] text-xl mb-4 font-semibold text-center md:text-left">
                        <LocalizedRichText
                          id="visibilitySnapshot.result.appearanceRate"
                          values={{
                            percentage: (
                              <span className="text-[#FF5E00]">
                                {Math.round(visibilityPercentage)}%
                              </span>
                            ),
                          }}
                        />
                      </p>
                      <h3 className="flex items-center gap-2 justify-center md:justify-start text-[#0A0A0A] font-medium text-base mb-4 text-center md:text-left">
                        <Info size={20} className="text-[#FF5E00]" />{" "}
                        {hasCompetitorAdvantage
                          ? t("visibilitySnapshot.result.competitorAdvantage")
                          : t("visibilitySnapshot.result.fastSample")}
                      </h3>
                      <h4 className="text-xs leading-4 text-[#737373] text-center md:text-left">
                        <LocalizedText
                          id="visibilitySnapshot.result.promptSample"
                          values={{
                            count:
                              completedPromptCount ||
                              t("visibilitySnapshot.result.limitedSet"),
                          }}
                        />
                      </h4>
                    </div>
                    <div
                      className={`flex flex-1 justify-center items-center relative`}
                    >
                      <CircularProgress
                        value={visibilityPercentage}
                      ></CircularProgress>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row gap-2 mb-4">
                    {/* competitors */}
                    <div className="bg-white group relative overflow-hidden flex shadow-lg flex-col px-5 pt-5 flex-1 border rounded-3xl">
                      <p className="text-base font-semibold text-[#0A0A0A] mb-[2px]">
                        <LocalizedText id="legacy.who.shows.up.with.you.2oou69" />
                      </p>
                      <p className="text-[#737373] text-xs font-regular mb-3">
                        {positionSummary}
                      </p>
                      <div>
                        {competitors.map((cm, index) => {
                          return (
                            <div
                              key={`${cm.title}-${index}`}
                              className={`${index === competitors.length - 1 && "blur-xs"} flex justify-between items-center`}
                            >
                              <div className="flex gap-1 justify-between items-center py-3">
                                <div className="relative flex w-6 h-6 mr-1 items-center justify-center rounded-md overflow-hidden">
                                  <img
                                    className="w-full h-full object-cover bg-white"
                                    src={getFaviconUrl(cm.url)}
                                    alt="logo"
                                  />
                                </div>
                                <div className="font-medium">{cm.title}</div>
                              </div>
                              <div className="text-[#0A0A0A] font-regular text-sm">
                                {cm.percentage}%
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <Button
                        tabIndex={-1}
                        onClick={handleStartTrial}
                        className="absolute cursor-pointer left-1/2 -bottom-1 -translate-x-1/2
                            translate-y-full
                            transition-transform duration-300 ease-out
                            group-hover:translate-y-[-30px]"
                      >
                        <LocalizedText id="legacy.view.details.1ctwhxm" />{" "}
                        <ArrowRight />
                      </Button>
                    </div>

                    {/* citations */}

                    <div className="bg-white group relative overflow-hidden flex shadow-lg flex-col px-5 pt-5 flex-1 border rounded-3xl">
                      <p className="text-base font-semibold text-[#0A0A0A] mb-[2px]">
                        <LocalizedText id="legacy.where.ai.pulls.information.from.yxp20c" />
                      </p>
                      <p className="text-[#737373] font-normal text-xs mb-3 font-sans">
                        <LocalizedText id="legacy.top.cited.domains.across.the.sample.14sa6iq" />
                      </p>
                      <div>
                        {citations.map((cm, index) => {
                          return (
                            <div
                              key={`${cm.url}-${index}`}
                              className={`${index === citations.length - 1 && "blur-xs"} flex justify-between items-center`}
                            >
                              <div className="flex gap-1 items-center justify-between py-3">
                                <div className="relative flex w-6 h-6 mr-1 items-center justify-center rounded-md overflow-hidden">
                                  <img
                                    className="w-full h-full object-cover bg-white"
                                    src={getFaviconUrl(cm.url)}
                                    alt="logo"
                                  />
                                </div>
                                <div>{cm.url}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <Button
                        tabIndex={-1}
                        onClick={handleStartTrial}
                        className="absolute left-1/2 cursor-pointer -bottom-1 -translate-x-1/2
                            translate-y-full
                            transition-transform duration-300 ease-out
                            group-hover:translate-y-[-30px]"
                      >
                        <LocalizedText id="legacy.view.details.1ctwhxm" />{" "}
                        <ArrowRight />
                      </Button>
                    </div>
                  </div>

                  <div className="group bg-white relative overflow-hidden flex mb-[40px] flex-col px-5 pt-5 flex-1 border rounded-3xl">
                    <p className="text-base font-semibold text-[#0A0A0A] mb-[12px]">
                      <LocalizedText id="legacy.suggested.next.steps.1c99g5i" />
                    </p>
                    <div className="flex flex-col gap-2">
                      {nextsteps.map((st, index) => {
                        return (
                          <div
                            key={`${st.title}-${index}`}
                            className={`${index === nextsteps.length - 1 && "blur-xs"} flex justify-between text-sm items-center bg-[#F5F5F5] rounded-md`}
                          >
                            <div className="flex justify-between p-2 items-center gap-2">
                              <div className="w-[20px] flex items-center justify-center h-[20px] rounded-full border border-[#a3a3a3] text-[#a3a3a3]">
                                {index + 1}
                              </div>
                              <span className="text-[#0A0A0A]">{st.title}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Button
                      tabIndex={-1}
                      onClick={handleStartTrial}
                      className="absolute cursor-pointer left-1/2 -bottom-1 -translate-x-1/2
                            translate-y-full
                            transition-transform duration-300 ease-out
                            group-hover:translate-y-[-30px]"
                    >
                      <LocalizedText id="legacy.view.details.1ctwhxm" />{" "}
                      <ArrowRight />
                    </Button>
                  </div>

                  <div
                    className={`flex flex-col justify-center items-center transition-all relative`}
                  >
                    <h2 className="text-3xl font-semibold text-center mb-1">
                      <LocalizedText id="legacy.get.your.ai.visibility.strategy.2k6rav" />
                    </h2>
                    <p className="text-[#737373] mb-4 text-center">
                      <LocalizedText id="legacy.unlock.full.data.and.a.free.ai.search.17brk1g" />
                    </p>
                    <Button
                      onClick={handleStartTrial}
                      className={`flex w-[180px] rounded-xl text-sm font-bold cursor-pointer py-[22px] px-[50px] hover:bg-[#ff8138] hover:text-white text-white bg-[linear-gradient(180deg,#FF5E00_0%,#A42301_100%)]`}
                    >
                      <LocalizedText id="legacy.start.free.trial.1dumtrn" />{" "}
                      <ArrowRight></ArrowRight>
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div
          style={{ backgroundImage: `url(${registrationBg})` }}
          className="p-9 bg-cover bg-center bg-no-repeat relative sticky flex justify-center top-0 hidden lg:flex flex-grow bg-black h-screen overflow-auto no-scrollbar"
        >
          <div className="w-full max-w-lg flex-col h-full flex items-start justify-start mt-[90px]">
            {!shouldShowSnapshotSkeleton ? (
              <div className="relative rounded-2xl w-16 h-16 overflow-hidden mb-12">
                <img
                  src={getFaviconUrl(brandDomain)}
                  alt={t("legacy.company.logo.1tv60tl")}
                  className="w-16 rounded-lg mb-5 absolute top-0 left-0 w-full h-full object-cover"
                />
                <div className="absolute -top-[1px] left-0 w-full h-1/5 bg-gradient-to-b from-[#906870] to-transparent pointer-events-none rounded-t-2xl" />
                <div className="absolute top-0 right-0 h-full w-1/5 bg-gradient-to-l from-[#906870] to-transparent pointer-events-none" />
              </div>
            ) : null}
            <AlertDialog>
              <AlertDialogTrigger className="w-full flex justify-center">
                <div
                  className={`absolute top-4 right-4 flex items-center justify-center text-[#FFFFFF] cursor-pointer px-4 py-2 rounded-full gap-2 text-sm bg-white/20`}
                >
                  <LogOut size={16} />
                  <LocalizedText id="legacy.log.out.mgztlb" />
                </div>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-center">
                    <LocalizedText id="legacy.are.you.sure.you.want.to.log.out.1y7m1mo" />
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-center">
                    <LocalizedText id="legacy.your.current.progress.will.be.saved.17clrki" />
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="cursor-pointer">
                    <LocalizedText id="legacy.cancel.18x3nqb" />
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleSignOut}
                    className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                  >
                    <LocalizedText id="legacy.log.out.mgz4hb" />
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {shouldShowSnapshotSkeleton ? (
              <OnboardingVisibilitySidebarLoading
                brandName={brandName}
                brandDomain={brandDomain}
                activeStep={activeSnapshotLoadingStep}
              />
            ) : (
              <>
                <p className="text-white text-2xl mb-6 w-full tracking-wider font-medium font-sans">
                  <LocalizedText id="legacy.here.is.your.ai.visibility.snapshot.1ksyt86" />
                </p>
                <div className="text-[#737373] w-full flex flex-col">
                  <p className="text-lg mb-12 max-w-md">
                    <LocalizedRichText
                      id="visibilitySnapshot.result.description"
                      values={{
                        brand: (
                          <span className="inline-flex items-baseline mx-1">
                            <span className="text-[#FF5E00] lowercase first-letter:uppercase">
                              {brandName}
                            </span>
                          </span>
                        ),
                      }}
                    />
                  </p>

                  <div className="text-base flex mb-6 gap-3 items-center">
                    <div className="flex flex-wrap">
                      <div className="flex w-fit items-center font-light gap-3 text-[#FAFAFA]">
                        <Check size={16}></Check>
                        <LocalizedText id="legacy.see.where.your.brand.is.already.visible.mntm4j" />
                      </div>
                    </div>
                  </div>
                  <p className="text-base flex mb-6 font-light gap-3 text-[#FAFAFA] items-center">
                    <Check size={16}></Check>
                    <LocalizedText id="legacy.understand.which.sources.ai.trusts.935hrx" />
                  </p>
                  <p className="text-base flex mb-6 font-light gap-3 text-[#FAFAFA] items-center">
                    <Check size={16}></Check>
                    <LocalizedText id="legacy.identify.gaps.where.competitors.win.17an3uy" />
                  </p>
                  <p className="text-base flex mb-12 font-light gap-3 text-[#FAFAFA] items-center">
                    <Check size={16}></Check>
                    <LocalizedText id="legacy.get.clear.actions.to.improve.your.visibility.91147l" />
                  </p>
                  <Button
                    onClick={handleStartTrial}
                    className={`flex w-[180px] rounded-xl text-sm font-bold cursor-pointer py-[22px] px-[50px] hover:bg-[#ff8138] hover:text-white text-white bg-[linear-gradient(180deg,#FF5E00_0%,#A42301_100%)]`}
                  >
                    <LocalizedText id="legacy.start.free.trial.1dumtrn" />{" "}
                    <ArrowRight></ArrowRight>
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
